import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { geocodeAddress, locationGeocodeQuery, type Coords } from '@/scripts/theme/geocode-cache'
import type { LocationMapModal } from '@/scripts/theme/location-map-modal'

export interface LocationData {
	handle: string
	name: string
	city: string
	cityHandle: string
	address: string
	postcode: string
	openingDays: string
	openingHours: string
	orderLink: string
	iconUrl: string
	imageUrl: string
	// Coords are filled in by the JS layer after geocoding, not stored on the metaobject.
	lat?: number
	lng?: number
}

const EARTH_RADIUS_KM = 6371

export class LocationList extends UcoastEl {
	static htmlSelector = 'location-list'

	private locations: LocationData[] = []
	private googleKey: string = ''
	private modalId: string = ''
	private gridItems: HTMLLIElement[] = []
	private tabs: HTMLButtonElement[] = []
	private forms: HTMLFormElement[] = []
	private mapTrigger?: HTMLButtonElement
	private resultBanner?: HTMLElement
	private coordsPromise?: Promise<void>

	override connectedCallback() {
		super.connectedCallback()

		this.googleKey = q.oa(this, 'data-google-key') ?? ''
		this.modalId = q.oa(this, 'data-modal-id') ?? ''

		this.locations = this.parseLocations()
		this.gridItems = Array.from(q.ol<HTMLLIElement>('[data-location-list-item]', this) ?? [])
		this.tabs = Array.from(q.ol<HTMLButtonElement>('[data-location-list-tab]', this) ?? [])
		this.mapTrigger = q.os<HTMLButtonElement>('[data-location-list-open-map]', this)
		this.resultBanner = q.os<HTMLElement>('[data-location-list-result]', this)

		this.forms = this.collectForms()

		this.bindTabs()
		this.bindForms()
		this.bindMapTrigger()
		this.bindModalListClicks()
		this.bindFindMeButtons()

		const defaultCity = q.oa(this, 'data-default-city')
		if (defaultCity) this.applyCityFilter(defaultCity)

		// NB: we intentionally do NOT prefetch coords on load. Geocoding every
		// store address for every visitor (most of whom never open the map or
		// search) was the bulk of our Google Geocoding API spend. Coords now
		// resolve lazily — on map open or a nearest-store search — via
		// getLocationsWithCoords().
	}

	private parseLocations(): LocationData[] {
		const raw = q.oa(this, 'data-locations')
		if (!raw) return []
		try {
			return JSON.parse(raw) as LocationData[]
		} catch (err) {
			console.warn('LocationList: failed to parse locations JSON', err)
			return []
		}
	}

	private collectForms(): HTMLFormElement[] {
		const inSection = Array.from(
			q.ol<HTMLFormElement>('[data-location-list-postcode-form]', this) ?? []
		)
		const modal = this.findModal()
		if (modal) {
			const inModal = Array.from(
				q.ol<HTMLFormElement>('[data-location-list-postcode-form]', modal) ?? []
			)
			return [...inSection, ...inModal]
		}
		return inSection
	}

	private findModal(): LocationMapModal | undefined {
		if (!this.modalId) return undefined
		const el = document.getElementById(this.modalId)
		return (el as LocationMapModal | null) ?? undefined
	}

	private bindTabs() {
		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				const cityHandle = tab.dataset.locationListTab
				if (!cityHandle) return
				this.applyCityFilter(cityHandle)
			})
		})
	}

	private applyCityFilter(cityHandle: string) {
		this.tabs.forEach((tab) => {
			tab.setAttribute('aria-selected', String(tab.dataset.locationListTab === cityHandle))
		})
		this.gridItems.forEach((item) => {
			const match = item.dataset.city === cityHandle
			if (match) {
				item.removeAttribute('hidden')
			} else {
				item.setAttribute('hidden', '')
			}
		})
	}

	private bindForms() {
		// Geocode on submit/blur only — never on keystrokes. Auto-searching
		// while typing fired a billable geocode on every debounced pause; a
		// postcode search is cheap to require an explicit submit for.
		this.forms.forEach((form) => {
			const runSearch = () => {
				const input = q.os<HTMLInputElement>('input[name="postcode"]', form)
				const value = input?.value.trim()
				if (!value) return
				void this.handlePostcode(value)
			}

			form.addEventListener('submit', (event) => {
				event.preventDefault()
				runSearch()
			})

			// Blur = the user finished entering a postcode and tabbed/clicked
			// away without pressing enter. One call, not one per pause.
			const input = q.os<HTMLInputElement>('input[name="postcode"]', form)
			input?.addEventListener('blur', () => {
				if ((input.value.trim().length ?? 0) < 3) return
				runSearch()
			})
		})
	}

	private bindMapTrigger() {
		if (!this.mapTrigger) return
		this.mapTrigger.addEventListener('click', () => {
			const modal = this.findModal()
			if (!modal) return
			modal.show(this.mapTrigger as HTMLElement)
		})
	}

	private bindModalListClicks() {
		const modal = this.findModal()
		if (!modal) return
		modal.addEventListener('click', (event) => {
			const target = event.target as HTMLElement | null
			const item = target?.closest('[data-location-list-modal-item]') as HTMLElement | null
			if (!item) return
			const handle = item.dataset.handle
			if (!handle) return
			const data = this.locations.find((l) => l.handle === handle)
			if (!data) return
			modal.focusLocation(data)
		})
	}

	// Resolves coordinates for every location in parallel. Geocoded results
	// are written back onto `this.locations` so subsequent reads see them.
	// Repeated calls share one in-flight promise.
	getLocationsWithCoords(): Promise<LocationData[]> {
		if (!this.coordsPromise) this.coordsPromise = this.prefetchCoords()
		return this.coordsPromise.then(() => this.locations)
	}

	private prefetchCoords(): Promise<void> {
		if (this.coordsPromise) return this.coordsPromise
		if (!this.googleKey || this.locations.length === 0) {
			this.coordsPromise = Promise.resolve()
			return this.coordsPromise
		}
		this.coordsPromise = Promise.all(
			this.locations.map(async (loc) => {
				if (typeof loc.lat === 'number' && typeof loc.lng === 'number') return
				const query = locationGeocodeQuery(loc.postcode, loc.city)
				if (!query) return
				const coords = await geocodeAddress(query, this.googleKey)
				if (coords) {
					loc.lat = coords.lat
					loc.lng = coords.lng
				}
			})
		).then(() => undefined)
		return this.coordsPromise
	}

	private async handlePostcode(postcode: string) {
		if (!this.googleKey) {
			this.setResult('Map is not configured — please contact the store.', true)
			return
		}
		if (this.locations.length === 0) {
			this.setResult('No locations available.', true)
			return
		}

		const [userCoords] = await Promise.all([
			geocodeAddress(postcode, this.googleKey),
			this.getLocationsWithCoords(),
		])

		if (!userCoords) {
			this.setResult('We couldn’t find that postcode. Please try again.', true)
			return
		}

		this.applySort(userCoords)
	}

	private bindFindMeButtons() {
		const sectionButtons = q.ol<HTMLButtonElement>('[data-location-list-find-me]', this) ?? []
		const modal = this.findModal()
		const modalButtons = modal
			? (q.ol<HTMLButtonElement>('[data-location-list-find-me]', modal) ?? [])
			: ([] as unknown as NodeListOf<HTMLButtonElement>)
		;[...Array.from(sectionButtons), ...Array.from(modalButtons)].forEach((btn) => {
			btn.addEventListener('click', () => this.requestUserLocation())
		})
	}

	private async requestUserLocation() {
		if (!('geolocation' in navigator)) {
			this.setResult('Your browser can’t share your location.', true)
			return
		}
		if (this.locations.length === 0) {
			this.setResult('No locations available.', true)
			return
		}

		await this.getLocationsWithCoords()

		navigator.geolocation.getCurrentPosition(
			(position) => {
				this.applySort({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				})
			},
			(error) => {
				const message =
					error.code === error.PERMISSION_DENIED
						? 'Location access denied. Try entering a postcode instead.'
						: 'Couldn’t get your location. Try entering a postcode instead.'
				this.setResult(message, true)
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
		)
	}

	private applySort(userCoords: Coords) {
		const candidates = this.locations.filter(
			(l): l is LocationData & Coords =>
				typeof l.lat === 'number' && typeof l.lng === 'number'
		)
		if (candidates.length === 0) {
			this.setResult('Store locations are still being resolved. Please try again in a moment.', true)
			return
		}

		const sorted = candidates
			.map((loc) => ({ loc, distance: haversine(userCoords, { lat: loc.lat, lng: loc.lng }) }))
			.sort((a, b) => a.distance - b.distance)

		const nearest = sorted[0]
		const distanceLabel = formatDistance(nearest.distance)
		this.setResult(`Closest store: ${nearest.loc.name} — ${distanceLabel}.`, false)

		this.updateCardDistances(sorted)
		this.reorderGrid(sorted)

		const modal = this.findModal()
		if (modal) {
			modal.renderSortedList(sorted)
			if (modal.hasAttribute('open')) {
				modal.focusLocation(nearest.loc)
			}
		}
	}

	private updateCardDistances(sorted: Array<{ loc: LocationData; distance: number }>) {
		// Reset every card's badge first so stale distances don't linger
		// on cards that weren't in this sort (e.g. missing coords).
		this.gridItems.forEach((item) => {
			const badge = q.os<HTMLElement>('[data-location-card-distance]', item)
			if (!badge) return
			badge.textContent = ''
			badge.setAttribute('hidden', '')
		})
		sorted.forEach(({ loc, distance }) => {
			const card = this.gridItems.find((item) => item.dataset.handle === loc.handle)
			if (!card) return
			const badge = q.os<HTMLElement>('[data-location-card-distance]', card)
			if (!badge) return
			badge.textContent = formatDistance(distance)
			badge.removeAttribute('hidden')
		})
	}

	private reorderGrid(sorted: Array<{ loc: LocationData; distance: number }>) {
		const grid = q.os<HTMLUListElement>('.location-list__grid', this)
		if (!grid) return

		// Skeleton-fade reorder:
		// 1. Fade every visible card into a skeleton state (translucent,
		//    slightly inset) — hides the upcoming DOM mutation.
		// 2. After the fade completes, reorder the cards silently.
		// 3. Reveal them in sorted order with a small stagger so the
		//    nearest cards appear first → reads as "closest moved closest".
		const FADE_OUT = 250
		const STAGGER = 70

		this.gridItems.forEach((card) => card.classList.add('is-sorting'))

		window.setTimeout(() => {
			sorted.forEach(({ loc }) => {
				const card = this.gridItems.find((item) => item.dataset.handle === loc.handle)
				if (card) grid.appendChild(card)
			})

			sorted.forEach(({ loc }, index) => {
				const card = this.gridItems.find((item) => item.dataset.handle === loc.handle)
				if (!card) return
				window.setTimeout(() => card.classList.remove('is-sorting'), index * STAGGER)
			})
		}, FADE_OUT)
	}

	private setResult(message: string, isError: boolean) {
		const allBanners: HTMLElement[] = []
		if (this.resultBanner) allBanners.push(this.resultBanner)
		const modal = this.findModal()
		if (modal) {
			const modalBanner = q.os<HTMLElement>('[data-location-list-result]', modal)
			if (modalBanner) allBanners.push(modalBanner)
		}
		allBanners.forEach((banner) => {
			banner.textContent = message
			banner.removeAttribute('hidden')
			banner.classList.toggle('location-list__result--error', isError)
		})
	}
}

function haversine(a: Coords, b: Coords): number {
	const lat1 = toRadians(a.lat)
	const lat2 = toRadians(b.lat)
	const dLat = toRadians(b.lat - a.lat)
	const dLng = toRadians(b.lng - a.lng)
	const h =
		Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
	const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
	return EARTH_RADIUS_KM * c
}

function toRadians(deg: number): number {
	return (deg * Math.PI) / 180
}

function formatDistance(km: number): string {
	if (km < 1) return `${Math.round(km * 1000)}m away`
	if (km < 10) return `${km.toFixed(1)}km away`
	return `${Math.round(km)}km away`
}
